import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
} from "lucide-react";

import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { Button } from "@/components/ui/Button";
import { Reveal } from "@/components/ui/Reveal";
import { SITE_CONTACT } from "@/data/site";
import { Turnstile } from "@/components/common/Turnstile";

const inputClasses =
  "h-11 w-full rounded-lg border border-input bg-background px-3.5 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [challengeVersion, setChallengeVersion] = useState(0);
  const inFlight = useRef(false);
  const requestId = useRef("");
  const [searchParams] = useSearchParams();
  const inquiryParam = searchParams.get("inquiry");
  const initialInquiry =
    inquiryParam === "practice"
      ? "physician-practice"
      : inquiryParam === "study"
        ? "study-opportunity"
        : "";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }
    const form = new FormData(event.currentTarget);
    if (!String(form.get("name") || "").trim()) {
      setError("Please enter your name.");
      return;
    }
    inFlight.current = true;
    setSending(true);
    setError("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      requestId.current ||= crypto.randomUUID();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ...Object.fromEntries(form.entries()),
          privacyConsent: form.get("privacyConsent") === "on",
          requestId: requestId.current,
          turnstileToken,
        }),
        signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) {
        throw new Error(typeof result.error === "string" ? result.error : "Unable to send your inquiry.");
      }
      requestId.current = "";
      setSubmitted(true);
      toast.success("Your inquiry has been sent to MedCore Research.");
    } catch (failure) {
      setError(failure instanceof Error && failure.message.startsWith("Please ")
        ? failure.message
        : "We couldn't confirm your inquiry was sent. Please try again or email support@medcoreresearch.com. Your entries have been kept.");
    } finally {
      window.clearTimeout(timeout);
      inFlight.current = false;
      setSending(false);
      setTurnstileToken("");
      setChallengeVersion(value => value + 1);
    }
  };

  return (
    <Section id="contact-form" className="scroll-mt-24" data-testid="contact-section">
      <Container className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12">
        <Reveal>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              Start a Conversation With MedCore
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Please select the option that best describes your inquiry. Do not
              include patient-identifiable or confidential clinical information.
            </p>

            {submitted ? (
              <div
                className="mt-8 flex flex-col items-center rounded-xl border border-primary/20 bg-primary/5 px-6 py-12 text-center"
                data-testid="contact-form-success-message"
                role="status"
              >
                <CheckCircle2 size={40} className="text-primary" />
                <h3 className="mt-4 text-lg font-semibold text-foreground">
                  Inquiry sent
                </h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Thank you for reaching out. Your inquiry has been submitted
                  to the MedCore Research team at support@medcoreresearch.com.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => setSubmitted(false)}
                >
                  Send another inquiry
                </Button>
              </div>
            ) : (
              <form className="mt-6" onSubmit={handleSubmit} data-testid="contact-form" aria-busy={sending}>
                {error && (
                  <p role="alert" className="mb-5 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    {error}
                  </p>
                )}
                <fieldset disabled={sending} className="min-w-0 space-y-5">
                <legend className="sr-only">Contact inquiry</legend>
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor="contact-website">Leave this field empty</label>
                  <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium">
                      Full name (required)
                    </label>
                    <input
                      id="name"
                      name="name"
                      maxLength={150}
                      type="text"
                      required
                      autoComplete="name"
                      placeholder="Jane Doe"
                      className={inputClasses}
                      data-testid="contact-form-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="organization" className="mb-1.5 block text-sm font-medium">
                      Organization (optional)
                    </label>
                    <input
                      id="organization"
                      name="organization"
                      maxLength={200}
                      type="text"
                      autoComplete="organization"
                      placeholder="Clinic or organization"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="mb-1.5 block text-sm font-medium">
                      Role (optional)
                    </label>
                    <input
                      id="role"
                      name="role"
                      maxLength={150}
                      type="text"
                      placeholder="Physician, coordinator, study lead..."
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
                      Email (required)
                    </label>
                    <input
                      id="email"
                      name="email"
                      maxLength={254}
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="jane@clinic.ca"
                      className={inputClasses}
                      data-testid="contact-form-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="inquiryType" className="mb-1.5 block text-sm font-medium">
                    Inquiry type (optional)
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    defaultValue={initialInquiry}
                    className={inputClasses}
                  >
                    <option value="">
                      Select an inquiry type
                    </option>
                    <option value="physician-practice">
                      I am a physician or healthcare practice interested in clinical research
                    </option>
                    <option value="pharmaceutical">
                      I represent a pharmaceutical company
                    </option>
                    <option value="biotechnology">
                      I represent a biotechnology company
                    </option>
                    <option value="cro">
                      I represent a Contract Research Organization
                    </option>
                    <option value="study-opportunity">
                      I would like to submit a study opportunity
                    </option>
                    <option value="other">
                      I am interested in another type of partnership
                    </option>
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className="mb-1.5 block text-sm font-medium">
                      Phone <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      maxLength={80}
                      type="tel"
                      autoComplete="tel"
                      placeholder="Optional contact number"
                      className={inputClasses}
                      data-testid="contact-form-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="therapeuticArea" className="mb-1.5 block text-sm font-medium">
                      Therapeutic area <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      id="therapeuticArea"
                      name="therapeuticArea"
                      maxLength={200}
                      type="text"
                      placeholder="e.g., cardiology"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="studyPhase" className="mb-1.5 block text-sm font-medium">
                      Study phase <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      id="studyPhase"
                      name="studyPhase"
                      maxLength={100}
                      type="text"
                      placeholder="e.g., Phase III"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="preferredContact" className="mb-1.5 block text-sm font-medium">
                      Preferred contact <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <select
                      id="preferredContact"
                      name="preferredContact"
                      defaultValue=""
                      className={inputClasses}
                    >
                      <option value="">No preference</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium">
                    Message (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    maxLength={5000}
                    rows={5}
                    placeholder="Tell us about your practice, study, or partnership inquiry."
                    className="w-full rounded-lg border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="contact-form-message-textarea"
                  />
                </div>

                <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/60 p-4 text-sm leading-relaxed text-muted-foreground">
                  <input
                    type="checkbox"
                    name="privacyConsent"
                    className="mt-1 h-4 w-4 rounded border-input accent-primary"
                  />
                  <span>
                    (Optional) I consent to MedCore Research using this information to
                    respond to my inquiry. I confirm that I have not included
                    patient-identifiable or confidential clinical information.
                    See our{" "}
                    <Link to="/privacy" className="font-medium text-primary underline underline-offset-2">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>

                <Turnstile key={challengeVersion} onToken={setTurnstileToken} onError={setError} />
                <Button type="submit" size="lg" className="w-full" disabled={sending || !turnstileToken} data-testid="contact-form-submit-button">
                  {sending ? "Sending…" : "Send Inquiry"}
                  <ArrowRight size={18} />
                </Button>
                </fieldset>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex h-full flex-col gap-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8" data-testid="contact-location-card">
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                Get in touch
              </h2>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">Location</p>
                    <p className="text-sm text-muted-foreground">
                      {SITE_CONTACT.addressLines.join(", ")}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <Mail size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">Email</p>
                    <a href={`mailto:${SITE_CONTACT.email}`} className="text-sm text-muted-foreground hover:text-primary">
                      {SITE_CONTACT.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Clock size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">Business Hours</p>
                    {SITE_CONTACT.hours.map((hours) => (
                      <p key={hours.days} className="text-sm text-muted-foreground">
                        {hours.days}: {hours.time}
                      </p>
                    ))}
                  </div>
                </li>
              </ul>
            </div>

            <div
              className="relative flex-1 overflow-hidden rounded-2xl border border-border shadow-sm"
              data-testid="contact-map"
            >
              <iframe
                title="MedCore Research location — Toronto, Ontario, Canada"
                src="https://www.google.com/maps?q=Toronto,Ontario,Canada&z=11&output=embed"
                className="h-full min-h-[260px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
